import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@@/contexts/AuthContext';
import { ResultValue } from '@@/types/ApiResult';
import { DfcApiClient } from '../api/dfc-api-client';
import { GetSuggestionsPayload } from '../schemas/entity';

export const SUGGESTIONS_QUERY_KEY = 'dfc-suggestions';

interface UseSuggestionsParams extends GetSuggestionsPayload {
  enabled?: boolean;
}

interface SuggestionResult {
  arn: string;
  name: string;
}

interface UseSuggestionsResult {
  resources: SuggestionResult[];
}

export const useSuggestions = (params: UseSuggestionsParams) => {
  const { instanceData } = useContext(AuthContext);

  const fetchSuggestions = async (): Promise<UseSuggestionsResult> => {
    if (!instanceData?.instanceId) {
      throw new Error('Missing required context data');
    }

    const dfcApiClient = new DfcApiClient();
    const result = await dfcApiClient.entities.getSuggestions({
      input: { namePrefix: params.namePrefix, type: params.type },
      queryString: {
        instanceId: instanceData.instanceId,
        region: instanceData.region
      }
    });

    if (!ResultValue.ok(result)) {
      throw new Error(result.message || 'Failed to fetch suggestions');
    }

    const data = result.data;

    return {
      resources: data.resources
    };
  };

  return useQuery({
    queryKey: [SUGGESTIONS_QUERY_KEY, params.type, params.namePrefix, instanceData?.instanceId, instanceData?.region],
    queryFn: fetchSuggestions,
    enabled: !!(instanceData?.instanceId && params.enabled !== false),
    staleTime: 30000
  });
};
